import { useState, useEffect, useCallback } from 'react';
import { matchService } from '../services';
import { MatchWithTeams } from '../types/database';

export const useMatches = () => {
  const [matches, setMatches] = useState<MatchWithTeams[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMatches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Loading matches...');
      const { data, error } = await matchService.getAllMatches();
      
      if (error) {
        console.error('❌ Error loading matches:', error);
        setError('Failed to load matches');
        return;
      }

      console.log('✅ Matches loaded successfully:', data?.length || 0);
      setMatches(data || []);
    } catch (err) {
      console.error('❌ Exception loading matches:', err);
      setError(err instanceof Error ? err.message : 'Failed to load matches');
    } finally {
      setLoading(false);
    }
  }, []);

  const filterMatchesByStatus = useCallback(async (status: string) => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await matchService.getAllMatches();
      
      if (error) {
        setError('Failed to load matches');
        return;
      }

      // Filter matches by status
      const filteredMatches = (data || []).filter(match => match.status === status);
      setMatches(filteredMatches);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load matches');
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleFavorite = useCallback(async (matchId: string) => {
    // TODO: Implement favorites functionality
    console.log('Toggle favorite for match:', matchId);
  }, []);

  const updateMatchScore = useCallback(async (matchId: string, homeScore: number, awayScore: number) => {
    // TODO: Implement score update functionality
    console.log('Update score for match:', matchId, homeScore, awayScore);
  }, []);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  // Listen for match score updates and refresh match list
  useEffect(() => {
    const handleMatchScoresUpdated = () => {
      console.log('🔄 Match scores updated, refreshing match list...');
      loadMatches();
    };

    window.addEventListener('matchScoresUpdated', handleMatchScoresUpdated);
    
    return () => {
      window.removeEventListener('matchScoresUpdated', handleMatchScoresUpdated);
    };
  }, [loadMatches]);

  return {
    matches,
    loading,
    error,
    loadMatches,
    filterMatchesByStatus,
    toggleFavorite,
    updateMatchScore
  };
};
